import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { addMonths, subMonths, startOfMonth, endOfMonth, subWeeks, addWeeks } from 'date-fns';

import {
  ReservationCalendar,
  CalendarSkeleton,
  MonthYearPicker,
  SearchableSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Separator,
  AttachmentGallery,
  useIsMobile,
  type YearMonth,
  type CalendarReservationItem,
  type CalendarCleaningData,
  type GalleryFile,
} from '@hotelise/ui';

import { getProperties } from 'state/modules/properties';
import { getPropertiesList, getPropertiesLoading } from 'state/modules/properties/selectors';
import {
  getReservations,
  setReservationsFilter,
} from 'state/modules/reservations';
import {
  getReservationsList,
  getReservationsLoading,
} from 'state/modules/reservations/selectors';
import { Property } from 'models/properties';
import { ReportTableItemWithTransfer } from 'interfaces/reports';
import { ComplexFilterType, DateFilterType } from 'interfaces/filters';
import type { ComplexFilterItem } from 'interfaces/filters';
import ReservationDetailsPanel from 'components/organisms/ReservationDetailsPanel';
import FileUploadService from 'services/storage/fileUpload';

// -- Helpers ------------------------------------------------------------------

type StatusFilter = 'all' | 'accepted' | 'cancelled';

function mapCleaningStatus(r: ReportTableItemWithTransfer, signedThumbnailUrl?: string): CalendarCleaningData {
  const cleaningData = r._item.cleaningData;
  if (!cleaningData) {
    return { status: 'planned' };
  }
  if (cleaningData.status === 'completed') {
    const imageCount = cleaningData.images?.length ?? 0;
    return {
      status: 'done',
      photos: imageCount > 0
        ? Array.from({ length: imageCount }, (_, i) => ({
            id: `${r._item.code}-cleaning-${i}`,
            url: i === 0 ? (signedThumbnailUrl ?? '') : '',
            name: `Cleaning photo ${i + 1}`,
          }))
        : undefined,
    };
  }
  return { status: 'in-progress' };
}

function mapToCalendarItem(r: ReportTableItemWithTransfer, signedThumbnailUrl?: string): CalendarReservationItem {
  const item = r._item;
  return {
    id: item.code,
    channel: item.channel?.toLowerCase() || 'manual',
    code: item.code,
    status: (item.status?.toLowerCase() || 'accepted') as CalendarReservationItem['status'],
    guestFirstName: item.guest?.first_name || '',
    guestLastName: item.guest?.last_name || '',
    guestPhoto: item.guest?.picture_url || undefined,
    checkin: new Date(item.start_date),
    checkout: new Date(item.end_date),
    nights: item.nights || 0,
    cleaning: mapCleaningStatus(r, signedThumbnailUrl),
  };
}

/**
 * Build Redux reservation filters for the calendar's visible month.
 * We extend the range by 1 week each side to capture cross-week bars.
 */
function buildCalendarFilters(
  propertyId: string,
  year: number,
  month: number,
): ComplexFilterItem[] {
  const monthStart = startOfMonth(new Date(year, month));
  const monthEnd = endOfMonth(new Date(year, month));

  const dateFrom = subWeeks(monthStart, 1).toISOString();
  const dateTo = addWeeks(monthEnd, 1).toISOString();

  return [
    {
      type: ComplexFilterType.PROPERTY_ID,
      value: propertyId,
    },
    {
      type: ComplexFilterType.DATE,
      value: {
        dateFrom,
        dateTo,
        dateFromType: DateFilterType.CHECKOUT_TIME,
        dateToType: DateFilterType.CHECKIN_TIME,
      },
    },
  ];
}

// -- Component ----------------------------------------------------------------

const CalendarPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const properties: Property[] = useSelector(getPropertiesList);
  const propertiesLoading = useSelector(getPropertiesLoading);
  const reservations: ReportTableItemWithTransfer[] = useSelector(getReservationsList);
  const reservationsLoading = useSelector(getReservationsLoading);

  const now = new Date();
  const [monthYear, setMonthYear] = useState<YearMonth>({
    year: now.getFullYear(),
    month: now.getMonth(),
  });
  const propertyParam = searchParams.get('property');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(propertyParam);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('accepted');
  const [selectedReservation, setSelectedReservation] = useState<ReportTableItemWithTransfer | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const hasRequestedRef = useRef(false);

  // Signed thumbnail URLs for cleaning photos (map: reservation code → signed URL)
  const [cleaningThumbnails, setCleaningThumbnails] = useState<Record<string, string>>({});

  // Cleaning gallery state (opened from calendar icon click)
  const [cleaningGalleryOpen, setCleaningGalleryOpen] = useState(false);
  const [cleaningGalleryFiles, setCleaningGalleryFiles] = useState<GalleryFile[]>([]);
  const [cleaningGalleryProperty, setCleaningGalleryProperty] = useState('');

  // Load properties on mount
  useEffect(() => {
    dispatch(getProperties());
  }, [dispatch]);

  // Auto-select property when no valid selection exists
  useEffect(() => {
    if (properties.length === 0) return;
    if (selectedPropertyId && properties.some((p) => p.id === selectedPropertyId)) return;
    setSelectedPropertyId(properties[0].id);
  }, [properties, selectedPropertyId]);

  // Keep URL in sync when property dropdown changes
  useEffect(() => {
    if (!selectedPropertyId) return;
    const current = searchParams.get('property');
    if (current !== selectedPropertyId) {
      setSearchParams({ property: selectedPropertyId }, { replace: true });
    }
  }, [selectedPropertyId, searchParams, setSearchParams]);

  // Fetch reservations when property or month changes
  useEffect(() => {
    if (!selectedPropertyId) return;

    hasRequestedRef.current = true;
    const filters = buildCalendarFilters(selectedPropertyId, monthYear.year, monthYear.month);
    dispatch(setReservationsFilter(filters));
    dispatch(getReservations());
  }, [dispatch, selectedPropertyId, monthYear.year, monthYear.month]);

  // Clear initial load only after the first fetch has completed
  useEffect(() => {
    if (initialLoad && hasRequestedRef.current && !reservationsLoading) {
      setInitialLoad(false);
    }
  }, [initialLoad, reservationsLoading]);

  const propertyItems = useMemo(
    () => properties.map((p) => ({ id: p.id, label: p.name })),
    [properties],
  );

  // Eagerly sign the first cleaning image for each reservation (for calendar thumbnail).
  // Skips reservations already cached in cleaningThumbnails to avoid re-signing on every
  // month/property navigation.
  useEffect(() => {
    const unsigned = reservations.filter(
      (r) =>
        r._item.cleaningData?.status === 'completed' &&
        r._item.cleaningData?.images?.[0]?.url &&
        !cleaningThumbnails[r._item.code],
    );
    if (!unsigned.length) return;

    let cancelled = false;
    const sign = async () => {
      const entries = await Promise.all(
        unsigned.map(async (r) => {
          const key = r._item.cleaningData!.images![0].url;
          try {
            const signed = await FileUploadService.getSignedUrl(key);
            return [r._item.code, signed] as [string, string];
          } catch {
            return null;
          }
        }),
      );
      if (!cancelled) {
        const map: Record<string, string> = {};
        for (const entry of entries) {
          if (entry) map[entry[0]] = entry[1];
        }
        setCleaningThumbnails((prev) => ({ ...prev, ...map }));
      }
    };
    sign();
    return () => { cancelled = true; };
  // cleaningThumbnails intentionally omitted — including it would cause infinite re-runs
  // as setCleaningThumbnails updates the map on every sign pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations]);

  // Handle cleaning photos click from calendar icon
  const handleCleaningPhotosClick = useCallback(
    async (item: CalendarReservationItem) => {
      const reservation = reservations.find((r) => r._item.code === item.id);
      const images = reservation?._item.cleaningData?.images;
      if (!images?.length) return;

      const propertyName = reservation?.property?.name || '';
      setCleaningGalleryProperty(propertyName);
      setCleaningGalleryFiles([]);
      setCleaningGalleryOpen(true);

      const files = await Promise.all(
        images.map(async (img, index) => {
          try {
            const signedUrl = await FileUploadService.getSignedUrl(img.url);
            return {
              id: `cleaning-${index}`,
              name: `Cleaning photo ${index + 1}`,
              url: signedUrl,
              size: 0,
              type: 'image/jpeg',
            } as GalleryFile;
          } catch {
            return null;
          }
        }),
      );

      const validFiles = files.filter(Boolean) as GalleryFile[];
      if (validFiles.length) setCleaningGalleryFiles(validFiles);
    },
    [reservations],
  );

  // Map reservations to calendar items, apply status filter client-side
  const calendarItems = useMemo(
    () => reservations
      .filter((r) => {
        if (statusFilter === 'all') return true;
        const status = r._item.status?.toLowerCase();
        return status === statusFilter;
      })
      .map((r) => mapToCalendarItem(r, cleaningThumbnails[r._item.code])),
    [reservations, statusFilter, cleaningThumbnails],
  );

  const prevMonth = useCallback(() => {
    const d = subMonths(new Date(monthYear.year, monthYear.month), 1);
    setMonthYear({ year: d.getFullYear(), month: d.getMonth() });
  }, [monthYear]);

  const nextMonth = useCallback(() => {
    const d = addMonths(new Date(monthYear.year, monthYear.month), 1);
    setMonthYear({ year: d.getFullYear(), month: d.getMonth() });
  }, [monthYear]);

  const goToday = useCallback(() => {
    const today = new Date();
    setMonthYear({ year: today.getFullYear(), month: today.getMonth() });
  }, []);

  // Keep selectedReservation in sync when Redux list refreshes
  useEffect(() => {
    if (!selectedReservation) return;
    const updated = reservations.find(
      (r) => r._item.code === selectedReservation._item.code,
    );
    if (updated && updated !== selectedReservation) {
      setSelectedReservation(updated);
    }
  }, [reservations]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReservationClick = useCallback(
    (item: CalendarReservationItem) => {
      const reservation = reservations.find((r) => r._item.code === item.id);
      if (reservation) {
        setSelectedReservation(reservation);
      }
    },
    [reservations],
  );

  const handlePropertyChange = useCallback(
    (id: string | null) => {
      setSelectedPropertyId(id || (properties[0]?.id ?? null));
    },
    [properties],
  );

  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === selectedPropertyId),
    [properties, selectedPropertyId],
  );

  // Count active secondary filters for the badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'accepted') count += 1;
    return count;
  }, [statusFilter]);

  // -- Legend (shared between mobile & desktop) -------------------------------
  const legend = (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400 inline-block" /> Airbnb
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-blue-500 inline-block" /> Booking.com
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400 inline-block" /> Other
      </span>
    </div>
  );

  // -- Mobile Toolbar ---------------------------------------------------------
  const renderMobileToolbar = () => (
    <div className="flex flex-col gap-2 pb-3">
      {/* Row 1: Month nav + Today + Filter toggle */}
      <div className="flex items-center gap-2">
        <MonthYearPicker value={monthYear} onChange={setMonthYear} className="flex-1 h-8 text-xs" />

        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={prevMonth} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={nextMonth} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={goToday}>
          Today
        </Button>

        <Button
          variant={filtersOpen ? 'secondary' : 'outline'}
          size="sm"
          className="h-8 px-2.5 shrink-0"
          onClick={() => setFiltersOpen(!filtersOpen)}
          aria-label="Toggle filters"
          aria-pressed={filtersOpen}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {activeFilterCount > 0 && (
            <span className="ml-1 text-[10px] bg-primary text-primary-foreground rounded-full h-4 w-4 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>

      {/* Row 2: Collapsible filters */}
      {filtersOpen && (
        <div className="flex flex-col gap-2">
          <SearchableSelect
            items={propertyItems}
            value={selectedPropertyId}
            onChange={handlePropertyChange}
            placeholder="Select property"
            searchPlaceholder="Search properties..."
            clearable={false}
            className="h-8 text-xs"
          />

          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger className="flex-1 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {legend}
        </div>
      )}
    </div>
  );

  // -- Desktop Toolbar --------------------------------------------------------
  const renderDesktopToolbar = () => (
    <div className="flex flex-wrap items-center gap-3 pb-4">
      <MonthYearPicker value={monthYear} onChange={setMonthYear} />

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={prevMonth} aria-label="Previous month">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={nextMonth} aria-label="Next month">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Button variant="outline" size="sm" onClick={goToday}>
        Today
      </Button>

      <Separator orientation="vertical" className="h-6" />

      <div className="w-[200px]">
        <SearchableSelect
          items={propertyItems}
          value={selectedPropertyId}
          onChange={handlePropertyChange}
          placeholder="Select property"
          searchPlaceholder="Search properties..."
          clearable={false}
          className="h-9"
        />
      </div>

      <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
        <SelectTrigger className="w-[150px] h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="accepted">Accepted</SelectItem>
          <SelectItem value="cancelled">Cancelled</SelectItem>
          <SelectItem value="all">All Statuses</SelectItem>
        </SelectContent>
      </Select>

      <div className="ml-auto">
        {legend}
      </div>
    </div>
  );

  // Show skeleton during initial page load
  if (initialLoad) {
    return <CalendarSkeleton showToolbar />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <p className="text-[26px] font-bold text-foreground m-0">Calendar</p>
      <p className="text-sm text-muted-foreground py-[5px] pb-[10px] m-0">
        {selectedProperty
          ? `${selectedProperty.name} — monthly reservation view`
          : 'Select a property to view reservations'}
      </p>

      {/* Toolbar */}
      {isMobile ? renderMobileToolbar() : renderDesktopToolbar()}

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        {!selectedPropertyId && !propertiesLoading && (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Select a property to view the calendar
          </div>
        )}

        {propertiesLoading && (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Loading properties...
          </div>
        )}

        {selectedPropertyId && (
          <ReservationCalendar
            year={monthYear.year}
            month={monthYear.month}
            reservations={calendarItems}
            onReservationClick={handleReservationClick}
            onCleaningPhotosClick={handleCleaningPhotosClick}
          />
        )}

        {reservationsLoading && selectedPropertyId && (
          <div className="text-center text-xs text-muted-foreground py-2">
            Loading reservations...
          </div>
        )}
      </div>

      {/* Reservation Detail Panel */}
      <ReservationDetailsPanel
        reservation={selectedReservation}
        open={selectedReservation !== null}
        onOpenChange={(v) => { if (!v) setSelectedReservation(null); }}
      />

      {/* Cleaning Photos Gallery (from calendar icon click) */}
      {cleaningGalleryFiles.length > 0 && (
        <AttachmentGallery
          open={cleaningGalleryOpen}
          onOpenChange={setCleaningGalleryOpen}
          files={cleaningGalleryFiles}
          title={`Cleaning Photos${cleaningGalleryProperty ? ` — ${cleaningGalleryProperty}` : ''}`}
          initialIndex={0}
        />
      )}
    </div>
  );
};

export default CalendarPage;
